import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

// Direct file import (not the `contexts` barrel) — this file is itself part of
// that barrel, so importing back from it is a circular import.
import { useSnackbar } from '../SnackbarContext/useSnackbar'
// VRG-252 — the family union comes from the shared registry, not a local re-declaration.
import type { TagFamily } from '@repo/types'

/**
 * Encoding-device (USB NFC reader) presence + scan stream, via the Vera Reader Helper.
 *
 * WHY a helper (not WebHID/WebUSB): the readers Vera uses are PC/SC (CCID) devices — e.g.
 * the ACS ACR1552 — which the OS smart-card driver claims exclusively. A browser has no
 * PC/SC API and can't claim a CCID interface, so WebHID/WebUSB can't see or use them. The
 * `apps/reader-helper` local service bridges the reader to the platform over a secure
 * localhost WebSocket; this context is its browser-side client.
 *
 * Security: the helper only accepts connections from Vera's own origin (it can't be
 * forged by a web page) and requires a pairing token the user enters once (stored in
 * localStorage, sent in the WS `hello`).
 */

const WS_PORT = (() => {
  const raw = import.meta.env.VITE_VERA_READER_WS_PORT
  const n = raw ? parseInt(String(raw), 10) : NaN
  return Number.isFinite(n) ? n : 48569
})()
const WS_URL = `ws://127.0.0.1:${WS_PORT}`
// Shown once per browser, right before the first-ever connect attempt (see the
// connect effect below).
const LOCAL_NETWORK_HINT_KEY = 'vera:reader:localNetworkHintShown'
// Set the first time the helper is reached from this browser. The ONLY evidence
// available that the Reader Helper is actually installed — see `helperEverSeen`.
const HELPER_SEEN_KEY = 'vera:reader:helperEverSeen'

// Vera-branded names for the readers we sell, mapped from the raw PC/SC device string so
// the UI shows "Vera Desk Reader" instead of "ACS ACR1552 1S CL Reader(2)". Unknown
// readers fall back to their raw name.
const READER_BRANDS: { match: RegExp; name: string }[] = [
  { match: /ACR1552/i, name: 'Vera Desk Reader' },
  // TODO(reader-brands): add the Vera Assembly Line Reader model once its device string is known.
]
function brandReaderName(raw?: string | null): string | null {
  if (!raw) return null
  const hit = READER_BRANDS.find((b) => b.match.test(raw))
  return hit ? hit.name : raw
}

type ScanHandler = (e: string) => void

/** Params to encode the NEXT tag presented to the reader (VERA-790 in-house encoding). */
export type EncodeParams = {
  /**
   * Lock the chip (default false). VRG-252 — WHAT THAT MEANS IS PER-FAMILY: on a 424 it is a
   * ChangeKey rotation to per-tag Vera keys (which come back in the result and must be stored);
   * on a Connect chip (NTAG 213 / EM near-field) it is the permanent static lock bits, which
   * produce no keys and cannot be undone by anyone.
   */
  lock?: boolean
  /** Verify origin embedded in the SUN URL (defaults to the helper's own default). */
  host?: string
  /** Fallback globalTagId when the reader can't report a hardware UID (mock/CLI). */
  tagId?: string
  /** Verification family from the page-1 tag-type selection. Default 424 DNA; the reader dispatches
   *  on the family's encode strategy and rejects one with no reader path (ST25) cleanly. */
  family?: TagFamily
}

/** Result of one encode — mirrors the reader-helper `encodeResult` payload. */
export type EncodeResult = {
  ok: boolean
  /** The chip's hardware UID (= the provisioned tag uid / globalTagId). */
  uid?: string | null
  url?: string
  locked?: boolean
  /** VRG-252 — 'static' or 'static+dynamic': how much of a Connect chip the lock actually covers.
   *  Absent on the 424 path (a key rotation has no page scope). */
  lockScope?: 'static' | 'static+dynamic'
  /** Present only when locked AND the family locks by key rotation (424) — the per-tag keys to
   *  persist as the verification secret (hex). A Connect lock produces no keys at all. */
  sdmMetaReadKey?: string
  sdmFileReadKey?: string
  error?: string
  /** Set when the presented chip class doesn't match the family this session is encoding. VRG-252
   *  made this symmetric: a Type-2 during a 424 session AND a 424 during a Connect session, since
   *  writing a proofless static URL onto a 424 permanently downgrades a chip that could carry a
   *  proof. The reader detects it up front and never attempts the write. */
  wrongTagType?: boolean
  /** VRG-252 — the presented Connect chip is already permanently locked, so it can never accept a
   *  write. Terminal per tag: re-tapping cannot help. */
  alreadyLocked?: boolean
  /** VRG-252 — the verify URL was written and confirmed, but the LOCK step failed (e.g. the NDEF
   *  overflows the static-lock range on a chip whose dynamic-lock address we don't know). The tag
   *  is usable but UNLOCKED — materially different from a failed write. */
  wroteButDidNotLock?: boolean
  /** VERA-790 tap-box-to-assign — set when the non-424 tag tapped mid-encode is a package HANDLE
   *  label (its NDEF `/pkg/<id>` URL). The session uses it to assign the encoded tags to that box. */
  packageHandleUrl?: string
  /** VERA-790 Phase 2 — a DIFFERENT session currently owns the reader (the single-session lock in
   *  the reader-helper). This arm was rejected outright — never even attempted. */
  busy?: boolean
  /** VRG-330 — the CALLER withdrew this arm via `cancelArm` (modal closed). Not an error and not a
   *  timeout: nothing failed, the result is simply no longer wanted. Callers must stay silent on it
   *  rather than reporting a failure the operator did not cause. */
  cancelled?: boolean
}

/** Result of one handle-encode — mirrors the reader-helper `encodeHandleResult` payload. */
export type EncodeHandleResult = {
  ok: boolean
  /** The chip's hardware UID. */
  uid?: string | null
  url?: string
  error?: string
  /** VERA-790 Phase 2 — see `EncodeResult.busy`. */
  busy?: boolean
  /** VERA-790 combined box flow — set (with `wrongTagType`) when a 424 product tag was presented
   *  during a resolve-or-register box scan. */
  wrongTagType?: boolean
  /** VERA-790 combined box flow — set (ok:true) when the scanned 213 was ALREADY a registered box:
   *  its existing `/pkg/<id>` handle URL, NOT overwritten. */
  existing?: boolean
  existingUrl?: string
  /** VRG-330 — see `EncodeResult.cancelled`. */
  cancelled?: boolean
}

type EncodingDeviceContextValue = {
  /** The reader feature is available in this build (helper client is active). */
  available: boolean
  /** The helper process is reachable + we're paired with it. */
  helperReachable: boolean
  /**
   * The one-time "Chrome may ask for local-network permission" heads-up hasn't been
   * seen yet. Surfaced ON the sidebar reader row (a warning dot + copy in its
   * popover) rather than as a toast, so the explanation sits with the control it's
   * about instead of floating over an unrelated page.
   */
  permissionHintPending: boolean
  /** Mark the heads-up as seen. Called automatically once the helper connects. */
  dismissPermissionHint: () => void
  /**
   * The helper has answered on this browser at least once, so it IS installed and
   * local-network access WAS granted. Lets the UI distinguish "you haven't
   * installed the Reader Helper" from "it isn't running / is being blocked" —
   * neither of which the browser will tell us directly.
   */
  helperEverSeen: boolean
  /**
   * Force a fresh connection attempt to the helper on 127.0.0.1, which is what
   * makes Chrome raise its Local Network Access prompt. There is no browser API to
   * request that permission directly — attempting the connection IS the request.
   */
  requestLocalNetworkAccess: () => void
  /** A reader is currently plugged into the helper. */
  isConnected: boolean
  /** The connected reader's name, if known. */
  readerName: string | null
  /** Helper is reachable but rejected our token (or we have none) — user must pair. */
  needsPairing: boolean
  /** Store a pairing token and (re)connect. */
  pair: (token: string) => void
  /** Forget the token and disconnect. */
  unpair: () => void
  /** Subscribe to reader scans (the `e=` token off a tag). Returns an unsubscribe fn. */
  subscribeScan: (cb: ScanHandler) => () => void
  /** Arm the reader to encode the NEXT tag presented, and resolve with the result. */
  encode: (params?: EncodeParams) => Promise<EncodeResult>
  /**
   * Arm the reader to write a PLAIN NDEF URL "handle" tag (VERA-790 Phase 1) to the NEXT tag
   * presented — NOT a SUN/verified product tag. Resolves with the result.
   */
  encodeHandle: (params: { url: string; resolveOrRegister?: boolean }) => Promise<EncodeHandleResult>
  /**
   * VRG-330 — withdraw this client's interest in any arm currently outstanding: every pending
   * `encode`/`encodeHandle` promise resolves NOW with `{ ok: false, cancelled: true }` instead of
   * parking for the full 310s timeout.
   *
   * A modal that arms the reader and is then closed used to leave its loop suspended inside
   * `await encodeHandle(...)` for up to five minutes, holding that closure's state alive, unable
   * to observe the close, and free to act on a result that arrived long after the operator had
   * moved on. Every surface that arms the reader has that shape — PackageBoxModal,
   * AddPackagesModal, EncodePackageHandleModal, CreateCertificatesModal — so the withdrawal
   * belongs here, once, rather than as a per-modal guard flag.
   *
   * ⚠️ THIS DOES NOT DISARM THE READER, and the distinction matters physically. The helper's WS
   * protocol has exactly four inbound messages — `hello`, `encode`, `encodeHandle`, `keepAlive`
   * (`apps/reader-helper/index.js`) — and no cancel among them. Its arm is a `pendingEncode` /
   * `pendingHandleEncode` slot that only a SUPERSEDING arm or its own 300s timer clears
   * (`apps/reader-helper/src/reader.js`). So a chip presented after `cancelArm()` is still
   * written; what changes is that nobody is listening for the result.
   *
   * A true disarm needs a `cancelEncode { requestId }` message on the helper, clearing the
   * matching pending slot and resolving it as cancelled, plus a release of the reader-owner lock.
   * That is a helper-protocol change and is deliberately NOT made here — VRG-329 is already
   * revisiting the helper's capability surface and should carry it.
   */
  cancelArm: () => void
  /**
   * VERA-790 Phase 2 — true while this session's last arm/keepAlive came back `busy` (a DIFFERENT
   * session currently owns the reader helper's single-session lock). Cleared the moment a
   * subsequent arm/keepAlive succeeds. Pages use this to show a "reader busy" message instead of
   * the generic stuck-tag one while they keep looping (they auto-claim once the other session
   * releases — on disconnect or 15s idle).
   */
  readerBusy: boolean
  /**
   * VERA-790 — true from the instant an `encode`/`encodeHandle` command is sent until the helper
   * acks that the reader is ARMED (or the result/timeout arrives). This is the brief "re-arming"
   * gap — e.g. between boxes in the package flow — during which a tap is missed. Distinct from the
   * long armed-and-waiting-for-a-tap period (that is `false`: the helper has acked `armed`). Pages
   * use it to show a short "Re-arming…" hint so the operator waits a beat before tapping.
   */
  isArming: boolean
  /**
   * VERA-790 Phase 2 — send a lightweight `keepAlive` over the WS to refresh this session's
   * ownership of the reader lock WITHOUT arming (an actively-waiting session looks "idle" to the
   * helper after 15s otherwise, letting a second tab steal it). Pages that hold a session open
   * drive their own ~10s interval calling this — the context doesn't run one itself, since only
   * the page knows when a session is actually "active" and holding the lock.
   */
  keepAlive: () => void

  // --- Back-compat with the previous WebHID API (kept so existing consumers work) ---
  /** @deprecated use `available` */
  hidConfigured: boolean
  /** @deprecated pairing replaces the WebHID device picker */
  requestDevice: () => Promise<void>
}

const EncodingDeviceContext = createContext<EncodingDeviceContextValue | null>(null)

export const EncodingDeviceProvider = ({ children }: { children: React.ReactNode }) => {
  const { open: notify } = useSnackbar()
  const [helperReachable, setHelperReachable] = useState(false)
  // Seeded from localStorage so the heads-up is shown once per browser, not once
  // per session. Read lazily — localStorage can throw in private mode.
  const [permissionHintPending, setPermissionHintPending] = useState(() => {
    try {
      return !window.localStorage.getItem(LOCAL_NETWORK_HINT_KEY)
    } catch {
      return false
    }
  })
  // Whether the helper has EVER answered on this browser.
  //
  // There is no way to detect the helper's installation, and no way to read
  // Chrome's Local Network Access permission — it isn't exposed to
  // navigator.permissions.query. So "unreachable" has three indistinguishable
  // causes: not installed, not running, or permission blocked. Guessing wrong
  // sends a first-time user to fix a Chrome setting when they simply haven't
  // downloaded anything.
  //
  // A prior successful connection is the one real signal we can keep: it proves
  // the helper WAS installed and permission WAS granted, so a later failure is
  // most likely "not running". Never connected → most likely "not installed", so
  // lead with the download instead of the permission.
  const [helperEverSeen, setHelperEverSeen] = useState(() => {
    try {
      return !!window.localStorage.getItem(HELPER_SEEN_KEY)
    } catch {
      return false
    }
  })

  const dismissPermissionHint = useCallback(() => {
    setPermissionHintPending(false)
    try {
      window.localStorage.setItem(LOCAL_NETWORK_HINT_KEY, 'true')
    } catch {
      /* private mode — the hint just reappears next load */
    }
  }, [])

  // Bump to force a fresh connection attempt. Reaching 127.0.0.1 from a public
  // origin is exactly what makes Chrome raise its Local Network Access prompt, so
  // "retry the connection" IS "show me the prompt" — there is no browser API to
  // request that permission directly.
  const [connectAttempt, setConnectAttempt] = useState(0)
  const requestLocalNetworkAccess = useCallback(() => setConnectAttempt(n => n + 1), [])
  // The hint describes a permission that has either been granted or not, and a
  // reachable helper PROVES it was granted — so reachability, not "you opened the
  // popover", is what retires it. Without this the popover could read "Reader
  // connected" and warn about the permission in the same breath, which is what it
  // did.
  useEffect(() => {
    if (!helperReachable) return
    dismissPermissionHint()
    setHelperEverSeen(true)
    try {
      window.localStorage.setItem(HELPER_SEEN_KEY, 'true')
    } catch {
      /* private mode — we just re-learn this next connect */
    }
  }, [helperReachable, dismissPermissionHint])

  const [isConnected, setIsConnected] = useState(false)
  const [readerName, setReaderName] = useState<string | null>(null)
  // Pairing is gone — the helper auto-connects on Vera's origin (see connect effect).
  // Kept as a constant so the back-compat interface fields still resolve.
  const needsPairing = false
  // VERA-790 Phase 2 — true while the reader lock is held by a DIFFERENT session. Derived from
  // the `busy` flag on the last encodeResult/encodeHandleResult/keepAliveAck (see onmessage below;
  // absent/false on a successful arm or claiming keepAlive, which clears it).
  const [readerBusy, setReaderBusy] = useState(false)
  // VERA-790 — the "re-arming" gap. `armingSentAt` maps an in-flight requestId → the ms timestamp
  // the arm command was sent, so `armed` (or the result) can both clear it AND log the real arm
  // latency ("measure this"). `isArming` is true while ANY arm is outstanding.
  const [isArming, setIsArming] = useState(false)
  const armingSentAt = useRef<Map<string, number>>(new Map())
  const clearArming = (requestId: string) => {
    const sentAt = armingSentAt.current.get(requestId)
    if (sentAt != null) {
      // eslint-disable-next-line no-console
      console.debug(`[reader] arm latency ${Date.now() - sentAt}ms (${requestId.slice(0, 8)})`)
      armingSentAt.current.delete(requestId)
    }
    setIsArming(armingSentAt.current.size > 0)
  }

  const wsRef = useRef<WebSocket | null>(null)
  const scanHandlers = useRef<Set<ScanHandler>>(new Set())
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closedByUs = useRef(false)
  // In-flight encode requests, keyed by requestId, resolved when the matching encodeResult arrives.
  const pendingEncodes = useRef<
    Map<string, { resolve: (r: EncodeResult) => void; timer: ReturnType<typeof setTimeout> }>
  >(new Map())
  // Separate pending map for handle-encode requests (VERA-790 Phase 1) — a distinct WS message
  // type (`encodeHandle`/`encodeHandleResult`), so it never collides with the SUN encode map above.
  const pendingHandleEncodes = useRef<
    Map<string, { resolve: (r: EncodeHandleResult) => void; timer: ReturnType<typeof setTimeout> }>
  >(new Map())

  const subscribeScan = useCallback((cb: ScanHandler) => {
    scanHandlers.current.add(cb)
    return () => scanHandlers.current.delete(cb)
  }, [])

  // Connect (and auto-reconnect) to the helper on mount. No pairing token: the helper
  // gates on Vera's Origin (which a web page cannot forge), so a valid-origin connection
  // is trusted and authed automatically. The platform just says hello and starts reading.
  useEffect(() => {
    closedByUs.current = false
    let ws: WebSocket | null = null

    const connect = () => {
      if (closedByUs.current) return
      try {
        ws = new WebSocket(WS_URL)
      } catch {
        scheduleReconnect()
        return
      }
      wsRef.current = ws

      ws.onopen = () => {
        // Announce ourselves; the helper auto-accepts on our origin (no token).
        try { ws?.send(JSON.stringify({ type: 'hello' })) } catch { /* noop */ }
      }
      ws.onmessage = (evt) => {
        let msg: any
        try { msg = JSON.parse(evt.data) } catch { return }
        if (msg?.type === 'paired' || msg?.type === 'ready') {
          setHelperReachable(true)
        } else if (msg?.type === 'reader') {
          setHelperReachable(true)
          setIsConnected(Boolean(msg.connected))
          setReaderName(brandReaderName(msg.name))
        } else if (msg?.type === 'scan' && typeof msg.e === 'string') {
          scanHandlers.current.forEach((cb) => {
            try { cb(msg.e) } catch { /* handler error shouldn't break the stream */ }
          })
        } else if (msg?.type === 'armed' && typeof msg.requestId === 'string') {
          // VERA-790 — the reader is now listening (armed). End the "re-arming" gap for this request.
          clearArming(msg.requestId)
        } else if (msg?.type === 'encodeResult' && typeof msg.requestId === 'string') {
          // VERA-790 Phase 2 — a busy rejection carries no requestId match issue (the helper still
          // echoes it back), so this also doubles as the busy-state tracker for a real arm.
          setReaderBusy(Boolean(msg.busy))
          clearArming(msg.requestId) // defensive: clear if `armed` was missed or the tap beat it
          const pending = pendingEncodes.current.get(msg.requestId)
          if (pending) {
            clearTimeout(pending.timer)
            pendingEncodes.current.delete(msg.requestId)
            const { type: _t, requestId: _r, ...result } = msg
            pending.resolve(result as EncodeResult)
          }
        } else if (msg?.type === 'encodeHandleResult' && typeof msg.requestId === 'string') {
          setReaderBusy(Boolean(msg.busy))
          clearArming(msg.requestId) // defensive: clear if `armed` was missed or the tap beat it
          const pending = pendingHandleEncodes.current.get(msg.requestId)
          if (pending) {
            clearTimeout(pending.timer)
            pendingHandleEncodes.current.delete(msg.requestId)
            const { type: _t, requestId: _r, ...result } = msg
            pending.resolve(result as EncodeHandleResult)
          }
        } else if (msg?.type === 'keepAliveAck') {
          // VERA-790 Phase 2 — a keepAlive has no pending-request map (fire-and-forget); it only
          // ever updates the busy flag so a page's heartbeat interval can react to losing the lock
          // (shouldn't normally happen for the active owner, but a helper restart / a foreign
          // session claiming during a network blip are real cases).
          setReaderBusy(Boolean(msg.busy))
        }
      }
      ws.onclose = () => {
        setHelperReachable(false)
        setIsConnected(false)
        wsRef.current = null
        scheduleReconnect()
      }
      ws.onerror = () => {
        try { ws?.close() } catch { /* noop */ }
      }
    }

    const scheduleReconnect = () => {
      if (closedByUs.current) return
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current)
      // Gentle retry — the helper may simply not be running yet.
      reconnectTimer.current = setTimeout(connect, 3000)
    }

    // The one-time "Chrome may ask for local-network permission" heads-up used to
    // fire here as a persistent top-center toast. It now lives on the sidebar
    // reader row (`permissionHintPending`), so the explanation sits next to the
    // reader control it describes instead of floating over whatever page you
    // happened to open. TRADE-OFF, stated plainly: a dot in the sidebar is less
    // likely to be read BEFORE Chrome's native dialog appears than a toast in the
    // middle of the screen was. It is the more discoverable-after, less
    // interruptive-before choice.

    connect()

    return () => {
      closedByUs.current = true
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current)
      try { ws?.close() } catch { /* noop */ }
      wsRef.current = null
    }
    // `connectAttempt` re-runs this effect, tearing down and re-opening the socket.
    // That fresh attempt at 127.0.0.1 is what raises Chrome's Local Network Access
    // prompt, which is the only way to surface it on demand.
  }, [connectAttempt])

  // Arm an encode of the next presented tag. Sends the WS `encode` command and resolves when the
  // helper reports the matching `encodeResult`. Rejects if the helper isn't reachable; times out
  // after 130s (the helper itself arms for 120s — give it a little headroom).
  const encode = useCallback((params: EncodeParams = {}): Promise<EncodeResult> => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return Promise.resolve({ ok: false, error: 'Reader helper is not connected' })
    }
    const requestId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `enc_${Date.now()}_${Math.round(Math.random() * 1e6)}`
    return new Promise<EncodeResult>((resolve) => {
      const timer = setTimeout(() => {
        if (pendingEncodes.current.has(requestId)) {
          pendingEncodes.current.delete(requestId)
          clearArming(requestId)
          resolve({ ok: false, error: 'No tag presented in time' })
        }
      }, 310000)
      pendingEncodes.current.set(requestId, { resolve, timer })
      try {
        ws.send(
          JSON.stringify({
            type: 'encode',
            requestId,
            dryRun: false,
            lock: params.lock === true,
            host: params.host,
            tagId: params.tagId,
            family: params.family || 'ntag424_dna',
          }),
        )
        armingSentAt.current.set(requestId, Date.now())
        setIsArming(true)
      } catch {
        clearTimeout(timer)
        pendingEncodes.current.delete(requestId)
        resolve({ ok: false, error: 'Failed to send encode command' })
      }
    })
  }, [])

  // Arm a handle-encode (VERA-790 Phase 1 — plain NDEF URL, no SUN/lock) of the next presented
  // tag. Mirrors `encode` above: sends the WS `encodeHandle` command, resolves on the matching
  // `encodeHandleResult`, same timeout headroom.
  const encodeHandle = useCallback((params: { url: string; resolveOrRegister?: boolean }): Promise<EncodeHandleResult> => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return Promise.resolve({ ok: false, error: 'Reader helper is not connected' })
    }
    const requestId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `enc_${Date.now()}_${Math.round(Math.random() * 1e6)}`
    return new Promise<EncodeHandleResult>((resolve) => {
      const timer = setTimeout(() => {
        if (pendingHandleEncodes.current.has(requestId)) {
          pendingHandleEncodes.current.delete(requestId)
          clearArming(requestId)
          resolve({ ok: false, error: 'No tag presented in time' })
        }
      }, 310000)
      pendingHandleEncodes.current.set(requestId, { resolve, timer })
      try {
        ws.send(JSON.stringify({ type: 'encodeHandle', requestId, url: params.url, resolveOrRegister: params.resolveOrRegister === true }))
        armingSentAt.current.set(requestId, Date.now())
        setIsArming(true)
      } catch {
        clearTimeout(timer)
        pendingHandleEncodes.current.delete(requestId)
        resolve({ ok: false, error: 'Failed to send encode command' })
      }
    })
  }, [])

  // VRG-330 — resolve every outstanding arm as cancelled. Cancels ALL of them rather than taking a
  // request id, because the helper holds ONE arm slot per kind and its owner lock admits one
  // session at a time, so "this client's outstanding arms" is at most the one the caller means.
  // Handing callers a request id to track would be a second bookkeeping surface for a set that
  // never exceeds one member.
  const cancelArm = useCallback(() => {
    pendingEncodes.current.forEach(({ resolve, timer }) => {
      clearTimeout(timer)
      resolve({ ok: false, cancelled: true, error: 'Arm cancelled' })
    })
    pendingEncodes.current.clear()
    pendingHandleEncodes.current.forEach(({ resolve, timer }) => {
      clearTimeout(timer)
      resolve({ ok: false, cancelled: true, error: 'Arm cancelled' })
    })
    pendingHandleEncodes.current.clear()
    // The reader stays armed (see the doc comment on `cancelArm` in the context type), so this
    // clears only OUR "re-arming…" indicator — the pending map is what `isArming` is derived from.
    armingSentAt.current.clear()
    setIsArming(false)
  }, [])

  // VERA-790 Phase 2 — fire-and-forget WS `keepAlive`, refreshing this session's ownership of the
  // reader lock without arming. No-op (silently) if the socket isn't open — the page's heartbeat
  // interval just tries again next tick; a dropped keepAlive isn't itself an error worth surfacing.
  const keepAlive = useCallback(() => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    try {
      ws.send(JSON.stringify({ type: 'keepAlive' }))
    } catch {
      /* noop — next interval tick retries */
    }
  }, [])

  // Back-compat no-ops — pairing was removed (auto-connect on origin).
  const pair = useCallback((_token: string) => { /* no-op: auto-connect */ }, [])
  const unpair = useCallback(() => { /* no-op: auto-connect */ }, [])
  const requestDevice = useCallback(async () => { /* no-op: auto-connect */ }, [])

  const value = useMemo<EncodingDeviceContextValue>(
    () => ({
      available: true,
      helperReachable,
      isConnected,
      readerName,
      needsPairing,
      permissionHintPending,
      helperEverSeen,
      dismissPermissionHint,
      requestLocalNetworkAccess,
      pair,
      unpair,
      subscribeScan,
      encode,
      encodeHandle,
      cancelArm,
      readerBusy,
      isArming,
      keepAlive,
      hidConfigured: true,
      requestDevice,
    }),
    [
      helperReachable,
      permissionHintPending,
      helperEverSeen,
      dismissPermissionHint,
      requestLocalNetworkAccess,
      isConnected,
      readerName,
      needsPairing,
      pair,
      unpair,
      subscribeScan,
      encode,
      encodeHandle,
      cancelArm,
      readerBusy,
      isArming,
      keepAlive,
      requestDevice,
    ],
  )

  return <EncodingDeviceContext.Provider value={value}>{children}</EncodingDeviceContext.Provider>
}

const defaultEncodingDeviceValue: EncodingDeviceContextValue = {
  available: false,
  helperReachable: false,
  isConnected: false,
  readerName: null,
  needsPairing: false,
  permissionHintPending: false,
  helperEverSeen: false,
  dismissPermissionHint: () => undefined,
  requestLocalNetworkAccess: () => undefined,
  pair: () => undefined,
  unpair: () => undefined,
  subscribeScan: () => () => undefined,
  encode: async () => ({ ok: false, error: 'Encoding device unavailable' }),
  encodeHandle: async () => ({ ok: false, error: 'Encoding device unavailable' }),
  cancelArm: () => undefined,
  readerBusy: false,
  isArming: false,
  keepAlive: () => undefined,
  hidConfigured: false,
  requestDevice: async () => undefined,
}

export function useEncodingDevice(): EncodingDeviceContextValue {
  const ctx = useContext(EncodingDeviceContext)
  return ctx ?? defaultEncodingDeviceValue
}
